// ════════════════════════════════════════════════════════════
//  Stands.jsx — THE FLOATING STADIUM, shared by Ringside AND the fight
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: "absolutely need the crowd with all their signs and stuff"
//  — in the FIGHT too, not only before the bell. So the stadium lives here once:
//  the island slabs and rocks, the crowd, the clouds that hold them up, the loose
//  debris, and the crowd's little moments (signs, beach balls, the wave).
//  <Stands cue director teams /> is everything. Ringside and BattleScene both mount it.
//  (The FloatingStands and Debris below were moved here, unchanged, from Ringside.jsx.)
// ════════════════════════════════════════════════════════════
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { mulberry32 } from "../ieBeingModels.jsx";
import StadiumCrowd, { ISLANDS, islandBob } from "./StadiumCrowd.jsx";
import IslandClouds from "./IslandClouds.jsx";
import CrowdProps from "./CrowdProps.jsx";
import { DEBRIS, HOLD, CROWD_PROPS } from "../data/ringside.js";
import { SKY, SKY_ANGLE, SKY_CLEAR } from "../data/skybox.js";

const ADD = THREE.AdditiveBlending;

// ── THE FLOATING STANDS — every section an island (data/ringside.js STADIUM) ──
// Each island = a terrace SLAB + a hanging ROCK + a Khao glow leaking from the
// rock, in its own group so it floats on its own rhythm (islandBob — the same
// formula the crowd rides, so they never part company).
// THE SLAB: a thin terrace spun round its arc — treads on top, a back wall,
// a flat underside. (A lathe can only taper in/out, never along the arc, so a
// keel built this way read as a flat wall from the ring — the rock is its own
// mesh below.)
function slabProfile(d) {
  const end = d.r0 + d.rows * d.dr, topY = d.y0 + (d.rows - 1) * d.dy, base = d.y0 - 1.4;
  const pts = [new THREE.Vector2(d.r0 + 0.4, base), new THREE.Vector2(d.r0, d.y0 - 0.6), new THREE.Vector2(d.r0, d.y0)];
  for (let i = 0; i < d.rows; i++) {                                    // the treads
    const y = d.y0 + i * d.dy;
    if (i) pts.push(new THREE.Vector2(d.r0 + i * d.dr, y));
    pts.push(new THREE.Vector2(d.r0 + (i + 1) * d.dr, y));
  }
  pts.push(new THREE.Vector2(end, topY + 0.8));                         // the back wall
  pts.push(new THREE.Vector2(end + 0.5, topY + 0.8));
  pts.push(new THREE.Vector2(end + 0.5, base));
  pts.push(new THREE.Vector2(d.r0 + 0.4, base));                         // closed
  return pts;
}
// THE ROCK: a lumpy, faceted cone hanging point-down under the slab — narrower
// at the bottom in EVERY direction, so it reads as a floating island from any
// angle. Seeded jitter, so every island's rock is its own and never changes.
export function rockGeo(seed, sides = 9) {   // exported: the Sky Box hangs on the same rock
  const g = new THREE.ConeGeometry(1, 1, sides, 4, false);
  g.rotateX(Math.PI);                 // point down
  g.translate(0, -0.5, 0);            // base at y 0, tip at y -1
  const R = mulberry32(seed), p = g.attributes.position, v = new THREE.Vector3();
  for (let k = 0; k < p.count; k++) {
    v.fromBufferAttribute(p, k);
    const depth = -v.y;               // 0 at the base, 1 at the tip
    const j = 0.16 + depth * 0.1;
    if (depth > 0.02 && depth < 0.98) v.set(v.x + (R() - 0.5) * j, v.y + (R() - 0.5) * 0.08, v.z + (R() - 0.5) * j);
    p.setXYZ(k, v.x, v.y, v.z);
  }
  g.computeVertexNormals();
  return g;
}
function FloatingStands() {
  const isles = useMemo(() => ISLANDS.map((isle, n) => {
    const d = isle.deck;
    const rims = [];
    // the lip of every tread glows a little, so the rows read at night.
    // A flat ring's angle θ = the lathe's φ − 90°.
    for (let i = 0; i < d.rows; i++) {
      const r = d.r0 + i * d.dr, y = d.y0 + i * d.dy + 0.02;
      const g = new THREE.RingGeometry(r, r + 0.09, 32, 1, isle.phi0 - Math.PI / 2, isle.len);
      g.rotateX(-Math.PI / 2); g.translate(0, y, 0);
      rims.push(g);
    }
    const lips = mergeGeometries(rims); rims.forEach((g) => g.dispose());
    const depth = d.rows * d.dr + 0.5, mid = d.r0 + depth / 2, width = isle.len * mid;
    const R = mulberry32(700 + n);
    // the main rock, and one or two smaller ones hanging off it so the keel is jagged
    const rocks = [{ geo: rockGeo(1000 + n), x: 0, z: 0, sx: width * 0.5, sy: d.keel, sz: depth * 0.62 }];
    const extra = 1 + Math.floor(R() * 2);
    for (let e = 0; e < extra; e++) {
      const side = e % 2 ? 1 : -1;
      rocks.push({ geo: rockGeo(2000 + n * 7 + e, 7), x: side * width * (0.18 + R() * 0.16), z: (R() - 0.5) * depth * 0.3,
        sx: width * (0.18 + R() * 0.1), sy: d.keel * (0.5 + R() * 0.3), sz: depth * (0.3 + R() * 0.15) });
    }
    return {
      slab: new THREE.LatheGeometry(slabProfile(d), 36, isle.phi0, isle.len),
      lips, rocks, mid, base: d.y0 - 1.3, ang: isle.phi0 + isle.len / 2,
    };
  }), []);
  // one material each, shared by every island (and the glow's pulse reaches them all)
  const mats = useMemo(() => ({
    slab: new THREE.MeshStandardMaterial({ color: "#1c1428", roughness: 0.92, metalness: 0.05, emissive: "#2a1648", emissiveIntensity: 0.35, side: THREE.DoubleSide }),
    rock: new THREE.MeshStandardMaterial({ color: "#221830", roughness: 0.95, metalness: 0.04, emissive: "#2a1648", emissiveIntensity: 0.4, flatShading: true }),
    glow: new THREE.MeshBasicMaterial({ color: "#7CE7FF", transparent: true, opacity: 0.28, side: THREE.BackSide, depthWrite: false, blending: ADD, toneMapped: false }),
    lips: new THREE.MeshBasicMaterial({ color: "#B388FF", transparent: true, opacity: 0.45, toneMapped: false, side: THREE.DoubleSide }),
  }), []);
  const refs = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    refs.current.forEach((g, i) => { if (g) g.position.y = islandBob(i, t); });
    mats.glow.opacity = 0.28 + Math.sin(t * 0.6) * 0.07;
  });
  return (
    <group>
      {isles.map((p, i) => (
        <group key={i} ref={(el) => (refs.current[i] = el)}>
          <mesh geometry={p.slab} material={mats.slab} />
          <mesh geometry={p.lips} material={mats.lips} />
          {/* the rock hangs from the slab's middle; local x runs along the arc, z points out */}
          <group position={[Math.sin(p.ang) * p.mid, p.base, Math.cos(p.ang) * p.mid]} rotation={[0, p.ang, 0]}>
            {p.rocks.map((r, k) => (
              <group key={k} position={[r.x, 0, r.z]} scale={[r.sx, r.sy, r.sz]}>
                <mesh geometry={r.geo} material={mats.rock} />
                {/* the Khao leaking out of the rock, like the Arena's own spire */}
                <mesh geometry={r.geo} material={mats.glow} scale={[1.07, 1.04, 1.07]} />
              </group>
            ))}
          </group>
        </group>
      ))}
    </group>
  );
}

// ── DEBRIS — loose rock adrift between the plate and the stands ──
function Debris({ cue }) {
  const mesh = useRef();
  const rocks = useMemo(() => {
    const R = mulberry32(909), D = DEBRIS;
    return Array.from({ length: D.count }, () => {
      const a = R() * Math.PI * 2; let r = D.rMin + R() * (D.rMax - D.rMin);
      if (r > D.clearR[0] && r < D.clearR[1]) r += D.clearR[1] - D.clearR[0];   // out of the Close Deck's ring
      // and out of the Sky Box's slice (data/skybox.js): nothing tumbles through the box or across its view
      const da = Math.abs(((a - SKY_ANGLE + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
      if (da < SKY_CLEAR.half && r < SKY.r + SKY.d / 2 + SKY_CLEAR.pad) r = Math.max(SKY.r + SKY.d / 2 + SKY_CLEAR.pad, D.clearR[1]) + (r - D.rMin) * 0.5;   // and still past the Close Deck's clear ring
      return { x: Math.sin(a) * r, z: Math.cos(a) * r, y: D.yMin + R() * (D.yMax - D.yMin),
        s: D.sizeMin + Math.pow(R(), 2.2) * (D.sizeMax - D.sizeMin),   // mostly small, a few big
        rx: R() * 6, ry: R() * 6, spin: (R() - 0.5) * 0.25, ph: R() * 6, sq: 0.55 + R() * 0.6 };
    });
  }, []);
  const o = useMemo(() => new THREE.Object3D(), []);
  useFrame(({ clock }) => {
    const m = mesh.current; if (!m) return;
    const t = clock.elapsedTime;
    // FRONT ROW: loose rock never drifts into the line from your seat to the plate (it shrinks away)
    const c = cue && cue.current, sx = c && c.hideOn ? c.hideX : 0, sz = c && c.hideOn ? c.hideZ : 0, on = !!(c && c.hideOn);
    const L2 = sx * sx + sz * sz;
    rocks.forEach((k, i) => {
      let fade = 1;
      if (on && L2 > 1) {
        const u = Math.max(0, Math.min(1, (k.x * sx + k.z * sz) / L2));
        const d = Math.hypot(k.x - sx * u, k.z - sz * u);
        fade = Math.max(0, Math.min(1, (d - (k.s * 1.8 + 3)) / 4));
      }
      o.position.set(k.x, k.y + Math.sin(t * 0.3 + k.ph) * 0.4, k.z);
      o.rotation.set(k.rx + t * k.spin, k.ry + t * k.spin * 0.7, 0);
      o.scale.set(k.s * fade, k.s * k.sq * fade, k.s * (1.6 - k.sq) * fade);
      o.updateMatrix(); m.setMatrixAt(i, o.matrix);
    });
    m.instanceMatrix.needsUpdate = true;
  });
  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, rocks.length]} frustumCulled={false}>
      <dodecahedronGeometry args={[1, 0]} />
      <meshStandardMaterial color="#241a33" roughness={0.95} emissive="#2a1648" emissiveIntensity={0.3} flatShading />
    </instancedMesh>
  );
}


// ── THE WHOLE STADIUM ──
export default function Stands({ cue, director, teams }) {
  return (
    <>
      <FloatingStands />
      <StadiumCrowd cue={cue} />
      <Debris cue={cue} />
      {/* what holds the islands up (data/ringside.js HOLD): clouds today; caP'n's Aethereal hand is pinned */}
      {HOLD === "clouds" && <IslandClouds />}
      {CROWD_PROPS.on && <CrowdProps cue={cue} director={director} teams={teams} />}
    </>
  );
}
